/** Error raised by the API client carrying the HTTP status so callers can branch and hint. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly body?: unknown,
    /** Base URL the failing request was sent to — lets hints name which backend rejected. */
    public readonly baseUrl?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** 4xx are user-facing (bad input, auth, quota) — not bugs worth reporting. */
  get isExpected(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

/**
 * A 401 can mean two very different things: the session genuinely expired, or the token is valid
 * but for a different environment than the backend you're pointed at trusts (e.g. a production
 * login hitting the dev backend). The backend tags the latter with `details.reason ===
 * 'issuer_not_trusted'`; we also fall back to the message text so older backends still trigger
 * the right hint. Telling these apart matters because "run `rekor login`" can never fix a
 * wrong-environment mismatch — it just re-mints a token the same backend still won't accept.
 */
/** The `error.details` object from a Rekor error envelope, or undefined when absent/non-object. */
function errorDetails(err: ApiError): Record<string, unknown> | undefined {
  const details = (err.body as { error?: { details?: unknown } } | undefined)?.error?.details;
  return details && typeof details === 'object' ? (details as Record<string, unknown>) : undefined;
}

function issuerMismatch(err: ApiError): { expectedIssuer?: string } | null {
  const details = errorDetails(err);
  if (details?.reason === 'issuer_not_trusted') {
    return { expectedIssuer: typeof details.expected_issuer === 'string' ? details.expected_issuer : undefined };
  }
  if (/issuer not recognized|signing key not found/i.test(err.message)) return {};
  return null;
}

/**
 * A 401 the backend tags `signing_key_unavailable`: the token IS for this environment, but its
 * signing key isn't (yet) in the backend's fetched JWKS — a transient WorkOS key-rotation publish
 * gap that self-heals in seconds-to-minutes. Distinct from `issuer_not_trusted`: re-login and
 * changing REKOR_API_URL are both useless here — the only fix is to wait and retry. Tag-only (no
 * message fallback) so we never misread a wrong-environment message as transient.
 */
function signingKeyUnavailable(err: ApiError): boolean {
  return errorDetails(err)?.reason === 'signing_key_unavailable';
}

/** A short actionable line for a known failure, or null when the raw message is enough. */
export function friendlyHint(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  switch (err.status) {
    case 401: {
      const at = err.baseUrl ? ` at ${err.baseUrl}` : '';
      if (signingKeyUnavailable(err)) {
        return (
          `The backend${at} could not resolve your token's signing key — this is usually a ` +
          'transient WorkOS key-rotation gap that clears on its own. Wait a few seconds and retry; ' +
          'you do not need to re-login or change your API URL.'
        );
      }
      const mismatch = issuerMismatch(err);
      if (mismatch) {
        const trusts = mismatch.expectedIssuer ? ` It trusts tokens from ${mismatch.expectedIssuer}.` : '';
        return (
          `The backend${at} does not recognize your token's issuer — your login likely belongs to a ` +
          `different environment than this backend.${trusts} Check REKOR_API_URL (and your configured ` +
          'api_url), then run `rekor login` against the matching environment.'
        );
      }
      return `Authentication failed or session expired${at}. Run \`rekor login\`.`;
    }
    case 402:
      return 'Quota exceeded for your plan. Review usage or upgrade your plan.';
    case 403:
      return 'Access denied. Your token may lack permission for this resource.';
    case 429:
      return 'Rate limited. Wait a moment and try again.';
    default:
      if (err.status >= 500) return 'Rekor server error. Try again shortly.';
      return null;
  }
}

/**
 * External-source upstream-rejection diagnostics carried in `error.details` (#505): the upstream HTTP
 * status + (bounded) response body, plus the secret-free request Rekor built. An HTTP rejection carries
 * `external_status` (+ `external_request`); a 200-logical-failure envelope carries only `external_body`.
 * Present only on a proxied read/write rejection; `null` otherwise.
 */
interface ProxyRequestDetail {
  method?: unknown;
  url?: unknown;
  body?: unknown;
}
function externalRejection(
  err: unknown,
): { status?: number; body?: unknown; request?: ProxyRequestDetail } | null {
  if (!(err instanceof ApiError)) return null;
  const d = errorDetails(err);
  if (!d) return null;
  const status = typeof d.external_status === 'number' ? d.external_status : undefined;
  const request =
    d.external_request && typeof d.external_request === 'object'
      ? (d.external_request as ProxyRequestDetail)
      : undefined;
  // Render the block whenever ANY external diagnostic is present — an HTTP rejection has a status/request,
  // an envelope failure (200-but-success:false) has only the response body. Without the last clause the
  // envelope path's bounded+scrubbed `external_body` would be REST-only and never shown in the CLI.
  if (status === undefined && !request && d.external_body === undefined) return null;
  return { status, body: d.external_body, request };
}

const EXTERNAL_DETAIL_PRINT_MAX = 2000;
/** Pretty-print a body (JSON if structured), bound it, and indent every line for the report block. */
function renderBody(body: unknown): string {
  let s: string;
  if (typeof body === 'string') s = body;
  else {
    try {
      s = JSON.stringify(body, null, 2);
    } catch {
      s = String(body);
    }
  }
  if (s.length > EXTERNAL_DETAIL_PRINT_MAX) s = `${s.slice(0, EXTERNAL_DETAIL_PRINT_MAX)}… (truncated)`;
  return s
    .split('\n')
    .map((l) => `  ${l}`)
    .join('\n');
}

/**
 * A multi-line diagnostic block for an external-source read/write rejection — the upstream response and
 * the request Rekor actually sent (`to_external`/destructure output, secrets redacted) — or `null` when
 * the error carries no such diagnostics (#505).
 */
export function externalRejectionReport(err: unknown): string | null {
  const ext = externalRejection(err);
  if (!ext) return null;
  const lines: string[] = [];
  if (ext.body !== undefined && ext.body !== null && ext.body !== '') {
    lines.push('Upstream response:');
    lines.push(renderBody(ext.body));
  }
  if (ext.request) {
    const method = typeof ext.request.method === 'string' ? ext.request.method : '';
    const url = typeof ext.request.url === 'string' ? ext.request.url : '';
    lines.push('Request Rekor sent:');
    const line = `${method} ${url}`.trim();
    if (line) lines.push(`  ${line}`);
    if (ext.request.body !== undefined) lines.push(renderBody(ext.request.body));
  }
  return lines.length ? lines.join('\n') : null;
}

/** Print a clean `Error: …` line plus a hint when we have one. Never prints a stack trace. */
export function handleCliError(err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  console.error(`Error: ${message}`);
  const hint = friendlyHint(err);
  if (hint) console.error(hint);
  // Proxied-write rejections carry the upstream response + the request Rekor built — render them so a
  // builder can diagnose a `field_mapping`/`to_external` against a real upstream without trial-and-error.
  const report = externalRejectionReport(err);
  if (report) console.error(report);
}
